import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  Text,
  VStack,
  FormControl,
  InputGroup,
  InputRightElement,
  IconButton,
  Flex,
} from '@chakra-ui/react';
import { CheckIcon, WarningIcon, CloseIcon } from '@chakra-ui/icons';
import {
  TAX_ID_OPTIONS,
  filterTaxIdOptions,
  getTaxIdOption,
} from '../../utils/taxIdOptions';
import {
  validateTaxId,
  hasBlockingValidationErrors,
} from '../../utils/taxIdValidation';

const TaxIdSelect = ({
  value = '',
  type = '',
  onChange,
  onValidationChange,
  ...rest
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [validation, setValidation] = useState({ isValid: null, message: '' });

  const dropdownRef = useRef(null);

  const filteredOptions = filterTaxIdOptions(TAX_ID_OPTIONS, searchTerm);
  const selectedOption = type ? getTaxIdOption(type) : null;
  const handleTypeSelect = (option) => {
    onChange({
      type: option.value,
      value: '',
    });
    setSearchTerm('');
    setIsDropdownOpen(false);

    setValidation({ isValid: null, message: '' });
    if (onValidationChange) {
      onValidationChange({
        isValid: null,
        message: '',
        hasBlockingErrors: false,
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setIsDropdownOpen(true);
  };
  const handleValueChange = (newValue) => {
    onChange({
      type,
      value: newValue,
    });

    if (type && newValue) {
      const validationResult = validateTaxId(newValue, type);
      setValidation(validationResult);

      if (onValidationChange) {
        onValidationChange({
          ...validationResult,
          hasBlockingErrors: hasBlockingValidationErrors(validationResult),
        });
      }
    } else {
      setValidation({ isValid: null, message: '' });
      if (onValidationChange) {
        onValidationChange({
          isValid: null,
          message: '',
          hasBlockingErrors: false,
        });
      }
    }
  };
  const handleClear = () => {
    onChange({
      type: '',
      value: '',
    });
    setSearchTerm('');
    setIsDropdownOpen(false);
    setValidation({ isValid: null, message: '' });
    if (onValidationChange) {
      onValidationChange({
        isValid: null,
        message: '',
        hasBlockingErrors: false,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const getValidationBorderColor = () => {
    if (validation.isValid === null) return 'gray.200';

    if (validation.validationLevel === 'strict') {
      return validation.isValid ? 'green.300' : 'red.300';
    } else {
      return validation.isValid ? 'blue.300' : 'orange.300';
    }
  };

  const getValidationIcon = () => {
    if (validation.isValid === null) return null;

    if (validation.validationLevel === 'strict') {
      return validation.isValid ? (
        <CheckIcon color="green.500" />
      ) : (
        <CloseIcon color="red.500" />
      );
    } else {
      return validation.isValid ? (
        <CheckIcon color="blue.500" />
      ) : (
        <WarningIcon color="orange.500" />
      );
    }
  };

  const getValidationTextColor = () => {
    if (validation.validationLevel === 'strict') {
      return validation.isValid ? 'green.600' : 'red.500';
    } else {
      return validation.isValid ? 'blue.600' : 'orange.500';
    }
  };

  return (
    <FormControl {...rest}>
      <VStack align="stretch" spacing={3}>
        <Text fontSize="sm" color="gray.600">
          Add your business tax ID to receive properly formatted invoices.
        </Text>

        <Box position="relative" ref={dropdownRef}>
          <InputGroup>
            <Input
              placeholder="Search tax ID types..."
              value={selectedOption?.label || searchTerm}
              onChange={handleSearchChange}
              onFocus={() => setIsDropdownOpen(true)}
              bg="white"
              borderRadius="12px"
              borderColor="gray.200"
              _focus={{ borderColor: 'blue.500' }}
            />
            {selectedOption && (
              <InputRightElement>
                <IconButton
                  size="xs"
                  variant="ghost"
                  icon={<CloseIcon />}
                  onClick={handleClear}
                  aria-label="Clear selection"
                />
              </InputRightElement>
            )}
          </InputGroup>

          {isDropdownOpen && (
            <Box
              position="absolute"
              top="100%"
              left={0}
              right={0}
              zIndex={1000}
              bg="white"
              border="1px"
              borderColor="gray.200"
              borderRadius="md"
              boxShadow="lg"
              maxHeight="300px"
              overflowY="auto"
              mt={1}
            >
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <Flex
                    key={option.value}
                    p={3}
                    cursor="pointer"
                    _hover={{ bg: 'gray.50' }}
                    onClick={() => handleTypeSelect(option)}
                    align="center"
                    justify="space-between"
                  >
                    <Flex align="center" gap={3}>
                      <Text fontSize="lg">{option.label.split(' ')[0]}</Text>
                      <Text fontWeight="medium" color="gray.900">
                        {option.label.split(' ').slice(1).join(' ')}
                      </Text>
                    </Flex>
                    <Text fontSize="sm" color="gray.500">
                      {option.country}
                    </Text>
                  </Flex>
                ))
              ) : (
                <Box p={3} textAlign="center" color="gray.500">
                  No tax ID types found
                </Box>
              )}
            </Box>
          )}
        </Box>

        {selectedOption && (
          <Box>
            <InputGroup>
              <Input
                value={value}
                onChange={(e) => handleValueChange(e.target.value)}
                placeholder={selectedOption.placeholder || 'Enter your tax ID'}
                bg="white"
                borderRadius="12px"
                borderColor={getValidationBorderColor()}
                _focus={{ borderColor: getValidationBorderColor() }}
              />
              {getValidationIcon() && (
                <InputRightElement>{getValidationIcon()}</InputRightElement>
              )}
            </InputGroup>

            {validation.message && (
              <Text
                fontSize="xs"
                color={getValidationTextColor()}
                mt={2}
                ml={2}
              >
                {validation.message}
              </Text>
            )}

            {validation.validationLevel === 'informational' &&
              validation.disclaimer && (
                <Text
                  fontSize="xs"
                  color="gray.500"
                  mt={3}
                  ml={2}
                  fontStyle="italic"
                >
                  ℹ️ {validation.disclaimer}
                </Text>
              )}

            <Text fontSize="xs" color="gray.500" mt={3} ml={2}>
              Tax IDs help ensure proper invoicing for your business.
            </Text>
          </Box>
        )}
      </VStack>
    </FormControl>
  );
};

export default TaxIdSelect;
